import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, Handshake, Mail, FileUp, Loader2, TrendingUp } from 'lucide-react';
import { authAPI } from '../../services/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await authAPI.getStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  const getUnreadText = (count) => {
    if (language === 'tr') return `${count} okunmamış`;
    return `${count} unread`;
  };

  const statCards = [
    { 
      labelKey: 'admin.dashboard.stats.totalLogs', 
      value: stats?.build_logs || 0, 
      icon: FileText, 
      link: '/admin/build-logs',
      color: '#00F0FF'
    },
    { 
      labelKey: 'admin.dashboard.stats.teamMembers', 
      value: stats?.team_members || 0, 
      icon: Users, 
      link: '/admin/team',
      color: '#D8B4FE'
    },
    { 
      labelKey: 'admin.dashboard.stats.sponsors', 
      value: stats?.sponsors || 0, 
      icon: Handshake, 
      link: '/admin/sponsors',
      color: '#4ADE80'
    },
    { 
      labelKey: 'admin.dashboard.stats.messages', 
      value: stats?.messages?.total || 0, 
      subValue: stats?.messages?.unread > 0 ? getUnreadText(stats.messages.unread) : null,
      icon: Mail, 
      link: '/admin/messages',
      color: '#F59E0B'
    },
    { 
      labelKey: 'admin.sidebar.documents', 
      value: stats?.documents || 0, 
      icon: FileUp, 
      link: '/admin/documents',
      color: '#EC4899'
    },
  ];

  const quickActions = [
    {
      to: '/admin/build-logs/new',
      icon: FileText,
      labelKey: 'admin.buildLogs.newLog',
      bg: 'rgba(0, 240, 255, 0.1)',
      border: 'rgba(0, 240, 255, 0.3)',
      color: '#00F0FF'
    },
    {
      to: '/admin/team/new',
      icon: Users,
      labelKey: 'admin.team.newMember',
      bg: 'rgba(216, 180, 254, 0.1)',
      border: 'rgba(216, 180, 254, 0.3)',
      color: '#D8B4FE'
    },
    {
      to: '/admin/sponsors/new',
      icon: Handshake,
      labelKey: 'admin.sponsors.newSponsor',
      bg: 'rgba(74, 222, 128, 0.1)',
      border: 'rgba(74, 222, 128, 0.3)',
      color: '#4ADE80'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#F8FAFC' }}>{t('admin.dashboard.title')}</h1>
        <p className="mt-2" style={{ color: '#94A3B8' }}>
          {t('admin.dashboard.welcome')}, Kartal Air
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        {statCards.map((card) => (
          <Link
            key={card.labelKey}
            to={card.link}
            className="glass-card p-6 transition-all duration-300 hover:scale-105"
            style={{
              borderColor: `${card.color}30`,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div 
                className="p-3 rounded-lg"
                style={{ background: `${card.color}20` }}
              >
                <card.icon size={24} style={{ color: card.color }} />
              </div>
              <TrendingUp size={16} style={{ color: '#4ADE80' }} />
            </div>
            <p className="text-3xl font-bold" style={{ color: '#F8FAFC' }}>{card.value}</p>
            <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>{t(card.labelKey)}</p>
            {card.subValue && (
              <p className="text-xs mt-2 px-2 py-1 rounded inline-block" style={{ 
                background: `${card.color}20`,
                color: card.color
              }}>
                {card.subValue}
              </p>
            )}
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>{t('admin.dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.labelKey}
              to={action.to}
              className="flex items-center gap-3 p-4 rounded-lg transition-all duration-300"
              style={{
                background: action.bg,
                border: `1px solid ${action.border}`,
                color: action.color
              }}
            >
              <action.icon size={20} />
              <span>{t(action.labelKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
